import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ReportPage } from "@/components/site/ReportPage";
import { REPORTS } from "@/content/reports";
import { reportSchemas } from "@/lib/seo/page-schema";
import { truncateAtWord } from "@/lib/seo/truncate";

async function loadReport(slug: string) {
  const report = REPORTS.find((r) => r.slug === slug);
  if (!report) return null;
  // Dynamic import keeps the ~70 KiB report-details module out of the
  // main/critical route bundle; it is fetched only when a report page loads.
  const { REPORT_DETAILS } = await import("@/content/report-details");
  const details = REPORT_DETAILS[slug];
  if (!details) return null;
  return { report, details };
}

const REMOVED_SLUGS = new Set([
  "work-from-home",
  "medical-laboratories-in-bulgaria",
  "customer-behavior-during-a-state-of-emergency",
  "what-happens-when-people-are-gone",
  "the-most-important-covid-19-related-information-for-see-region",
  "the-most-popular-baby-products-a-social-media-analysis",
  "bulgarian-telecom-providers-a-social-media-analysis",
]);

export const Route = createFileRoute("/reports/$slug")({
  loader: async ({ params }) => {
    if (params.slug === "index") {
      throw redirect({ to: "/reports", statusCode: 301 });
    }
    if (params.slug === "media-intelligence-banking-sector-bulgaria") {
      throw redirect({ to: "/reports/$slug", params: { slug: "bulgarian-banking-industry-report" }, statusCode: 301 });
    }
    if (REMOVED_SLUGS.has(params.slug)) {
      throw notFound();
    }
    const data = await loadReport(params.slug);
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Report - Perceptica" }] };
    }
    const { report, details } = loaderData;
    const headline = details.headline ?? report.title;
    const desc = details.metaDescription ?? report.metaDescription ?? truncateAtWord(details.excerpt ?? details.intro[0] ?? "");
    const pageTitle = `${headline} | Perceptica Report`;
    const canonical = `https://perceptica.com/reports/${report.slug}`;
    const shareImage = !details.heroImage.src ? "" : details.heroImage.src.startsWith("/")
      ? `https://perceptica.com${details.heroImage.src}`
      : details.heroImage.src;
    return {
      meta: [
        { title: pageTitle },
        { name: "description", content: desc },
        { property: "og:title", content: headline },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        ...(shareImage ? [{ property: "og:image", content: shareImage }, { property: "og:image:alt", content: details.heroImage.alt }, { name: "twitter:image", content: shareImage }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: headline },
        { name: "twitter:description", content: desc },
      ],
      scripts: reportSchemas({
        locale: "en",
        slug: report.slug,
        title: headline,
        description: desc,
        datePublished: details.publishedDate,
        analyst: details.analystName ? { name: details.analystName, role: details.analystRole } : undefined,
        image: shareImage || undefined,
        year: report.year,
        dateModified: report.updatedAt,
        topic: report.topic,
      }),
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-[760px] px-6 py-24 text-center">
      <h1 className="font-display text-3xl font-medium text-foreground">Report not found</h1>
      <p className="mt-4 text-foreground/70">It may have been moved or unpublished.</p>
    </div>
  ),
  component: ReportRoute,
});

function ReportRoute() {
  const { report, details } = Route.useLoaderData();
  return <ReportPage report={report} details={details} />;
}
